import React, { useCallback } from 'react';

import {
  adminCreateEntitlement,
  adminDeleteLead,
  allLeadsSelector,
  CampaignStatusType,
  ConfirmationTypes,
  inviteLeads,
} from '@pharmaplan/common';

import { useNavigate } from 'react-router-dom';
import { FormikProps } from 'formik';
import { Typography } from '@mui/material';
import useTableSelectCheckbox from '../../useTableSelectCheckbox';
import { useAppSelector } from '../../useAppSelector';
import { Constants } from '../../../helpers/Constants';
import { useAppDispatch } from '../../useAppDispatch';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';
import { setDialog } from '../../../reducers/dialogReducer';
import { selectAllTypes } from '../../../components/Admin/Leads/LeadsHeader';
import { LeadTabs } from '../../../components/Admin/Leads/Leads';
import strings from '../../../localization';
import CampaignDetails from '../../../components/Admin/Leads/CampaignDetails';
import LeadDetails from '../../../components/Admin/Leads/LeadDetails';

const { createLead, editLead } = Constants.paths.admin;

const useLeads = (
  page: number,
  formik: FormikProps<any>,
  selectedTabs: LeadTabs,
) => {
  const dispatch = useAppDispatch();
  const list = useAppSelector(allLeadsSelector);
  const navigate = useNavigate();

  const handleCreate = () => {
    navigate(createLead, { state: { userType: selectedTabs } });
  };

  const handleEdit = (leadId: string) =>
    () => {
      navigate(editLead, { state: { leadId, userType: selectedTabs } });
    };

  const handleDelete = (leadId: string) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <ConfirmationModal
              type={ConfirmationTypes.deleteEntry}
              confirmAction={() => {
                dispatch(adminDeleteLead(leadId));
              }}
            />
          ),
          showCloseButton: false,
        }),
      );
    };

  const addCredits = (ids: Array<string>) =>
    () => {
      dispatch(adminCreateEntitlement(ids));
    };

  const handleAddCredits = (ids: Array<string>, credits: number) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <ConfirmationModal
              type={ConfirmationTypes.addCredits}
              confirmAction={addCredits(ids)}
              customMessage={(
                <Typography sx={{ fontSize: 14, textAlign: 'center' }}>
                  {strings.formatString(strings.creditAddPrompt, credits)}
                </Typography>
            )}
            />
          ),
          heading: {
            title: '',
          },
          showCloseButton: false,
        }),
      );
    };

  const {
    selectedIds,
    setSelectedIds,
    handlePress,
    allIds,
    handleReset,
    handleAllIdsAdd,
    handleSelectedIds,
    handleAllIdsRemoval,
    handleSelectAll,
  } = useTableSelectCheckbox({
    page,
    useOfEdit: false,
    userType: 'leads',
    dataLength: list?.data?.length ?? 0,
  });

  const getIdsByTypes = useCallback(
    (types: Array<selectAllTypes>) => {
      const data = list?.data ?? [];

      return data
        .filter((item) => {
          const isExistingUser = (!!item.pharmacyId || !!item.professionalId)
            && !item.lastInviteSentAt
            && !item.inviteSentCount;

          const isActive = item.campaignStatus === CampaignStatusType.Active;
          const includePharmacies = types.includes(selectAllTypes.pharmacies);
          const includeLeads = types.includes(selectAllTypes.leads);

          const isLeadType = includeLeads && !item.pharmacyId && !item.professionalId;
          const isPharmacyType = includePharmacies && isExistingUser;

          return (isLeadType || isPharmacyType) && isActive;
        })
        .map((item) =>
          item.leadId);
    },
    [list?.data],
  );

  const applySelectAllTypes = useCallback(
    (types: Array<selectAllTypes>) => {
      const ids = getIdsByTypes(types);

      if (!ids.length) {
        const pageIds = (list?.data ?? []).map((item) =>
          item.leadId);
        handleSelectedIds({ [page]: [] });
        handleAllIdsRemoval(pageIds);
        handleSelectAll({ [page]: false });
        return;
      }

      handleSelectedIds({ [page]: ids });
      handleAllIdsAdd(ids);
      handleSelectAll({ [page]: true });
    },
    [
      getIdsByTypes,
      handleAllIdsAdd,
      handleAllIdsRemoval,
      handleSelectAll,
      handleSelectedIds,
      list?.data,
      page,
    ],
  );

  const sendInvitationToLeads = (ids: Array<string>) =>
    () => {
      dispatch(inviteLeads(ids));
    };

  const handleInviteSelected = () => {
    const types: Array<selectAllTypes> = (
      formik.values?.selectAllType ?? []
    ).map(Number);

    const data = list?.data ?? [];

    const leadsToInvite = selectedIds.filter((id) => {
      const lead = data.find((item) =>
        item.leadId === id);
      if (!lead) return false;
      return types.includes(selectAllTypes.leads) && !lead.pharmacyId;
    });

    const pharmaciesToCredit = selectedIds.filter((id) => {
      const lead = data.find((item) =>
        item.leadId === id);
      if (!lead) return false;
      const isExistingUser = !!lead.pharmacyId && !lead.lastInviteSentAt && !lead.inviteSentCount;
      return types.includes(selectAllTypes.pharmacies) && isExistingUser;
    });

    if (leadsToInvite.length) {
      sendInvitationToLeads(leadsToInvite)();
    }
    if (pharmaciesToCredit.length) {
      addCredits(pharmaciesToCredit)();
    }
  };

  const handleCampaignDetails = (payload: { campaignId: string | null }) =>
    () => {
      dispatch(
        setDialog({
          heading: { title: strings.campaignDetails },
          showCloseButton: true,
          // eslint-disable-next-line react/jsx-props-no-spreading
          Component: <CampaignDetails {...payload} />,
        }),
      );
    };

  const handleDetails = (payload: {
          name: string;
          email: string;
          phone: string;
          statusLabel: string;
          statusStyle: Record<string, any>;
          notes: string;
        }) =>
    () => {
      dispatch(
        setDialog({
          heading: { title: strings.leadDetails },
          showCloseButton: true,
          // eslint-disable-next-line react/jsx-props-no-spreading
          Component: <LeadDetails {...payload} />,
        }),
      );
    };

  return {
    selectedIds,
    setSelectedIds,
    handlePress,
    allIds,
    handleAddCredits,
    handleReset,
    handleDelete,
    handleEdit,
    sendInvitationToLeads,
    handleCreate,
    handleInviteSelected,
    handleCampaignDetails,
    handleDetails,
    applySelectAllTypes,
  };
};

export default useLeads;
